import { useCallback, useEffect, useState } from "react";
import { Plus, Sparkles, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = { term: "", mode: "keep", spanish: "" };

const Suggestions = ({ items, canEdit, onAccept, onDismiss }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="glossary-suggestions">
    <div className="flex items-center gap-2 text-sm font-medium mb-3"><Sparkles className="h-4 w-4 text-blue-600" /> Suggested from your first scan</div>
    <div className="flex flex-wrap gap-2">
      {items.map((g) => (
        <span key={g.id} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full border border-slate-300 text-sm" data-testid={`suggestion-${g.id}`}>
          {g.term}
          <button disabled={!canEdit} onClick={() => onAccept(g)} className="p-1 rounded-full hover:bg-emerald-50 text-emerald-700" aria-label="Keep as is" data-testid={`suggestion-accept-${g.id}`}><Check className="h-3.5 w-3.5" /></button>
          <button disabled={!canEdit} onClick={() => onDismiss(g)} className="p-1 rounded-full hover:bg-red-50 text-red-600" aria-label="Dismiss" data-testid={`suggestion-dismiss-${g.id}`}><X className="h-3.5 w-3.5" /></button>
        </span>
      ))}
    </div>
  </div>
);

export default function Glossary() {
  const { session } = useSession();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blank);

  const load = useCallback(() => api.get("/glossary").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load, session.org_id]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/glossary", form);
      setForm(blank);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const accept = async (g) => { try { await api.patch(`/glossary/${g.id}`, { ...g, status: "active" }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (g) => { try { await api.delete(`/glossary/${g.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  const suggested = rows.filter((r) => r.status === "suggested");
  const active = rows.filter((r) => r.status === "active");

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">Glossary</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Words the AI must respect</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-xl">Brand, service and city names. Every translation follows these rules. They belong to this business only and are never shared.</p>
      </div>
      {suggested.length > 0 && <Suggestions items={suggested} canEdit={session.can_edit} onAccept={accept} onDismiss={remove} />}
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1.3fr_180px_1.3fr_auto] gap-2 bg-white border border-slate-200 rounded-lg p-4">
        <Input placeholder="Term in English" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} required data-testid="glossary-term-input" />
        <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
          <SelectTrigger data-testid="glossary-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="keep" data-testid="glossary-mode-keep">Keep as is</SelectItem>
            <SelectItem value="fixed" data-testid="glossary-mode-fixed">Fixed Spanish word</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder={form.mode === "fixed" ? "Spanish word" : "Stays in English"} disabled={form.mode === "keep"} value={form.spanish} onChange={(e) => setForm({ ...form, spanish: e.target.value })} required={form.mode === "fixed"} data-testid="glossary-spanish-input" />
        <Button type="submit" disabled={!session.can_edit} data-testid="glossary-add-button"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </form>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="glossary-list">
        {active.length === 0 && <div className="p-6 text-sm text-slate-500" data-testid="glossary-empty">No terms yet.</div>}
        {active.map((g) => (
          <div key={g.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1.3fr_1fr_1.3fr_auto] gap-3 items-center px-4 py-3 text-sm" data-testid={`glossary-row-${g.id}`}>
            <span className="font-medium">{g.term}</span>
            <span className="hidden sm:block text-xs font-mono uppercase text-slate-500">{g.mode === "keep" ? "keep as is" : "fixed word"}</span>
            <span className="hidden sm:block text-slate-900">{g.mode === "fixed" ? g.spanish : "–"}</span>
            <Button variant="ghost" size="icon" disabled={!session.can_edit} onClick={() => remove(g)} data-testid={`glossary-delete-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
